import React, { useEffect, useState } from "react";
import { RestockItem, VariationItem } from "../../../@types/inventory";
import { getInventory, restockInventory } from "../../../backend-services";
import { useInventoryStore } from "../../../state/useInventoryStore";
import { EditInventoryForm } from "../restock/EditInventoryForm";
import { CloseIcon } from "../../../assets/icons";
import BackArrow from "../../../assets/icons/backArrow";
import { convertToFloat } from "../../../utils/convertToFloat";
import {
  ValidateVariation,
  validateRestock,
} from "../../../modules/inventory/inventory";
import PageLoader from "../../common/PageLoader";
import { useTranslation } from "react-i18next";
import { showToast } from "../../../utils/toastUtils";
import LoadingSpinner from "../../common/loadingSpinner";
import ButtonCTA from "../../Reusable/ButtonCTA";

interface VariationError {
  type: string;
  costPrice: string;
  price: string;
  quantity: string;
}

interface RestockProps {
  setOpenModal: (show: boolean) => void;
  restockId: string | undefined;
  fetchDetail?: (id: string) => void;
  fetchInventory?: () => void;
}

const Restock = ({
  restockId,
  setOpenModal,
  fetchDetail,
  fetchInventory,
}: RestockProps) => {
  const [inventories, setInventories] = useState<RestockItem>({
    id: restockId,
    name: "",
    rate: "",
    costPrice: "",
    quantity: "",
    date: "",
  });
  const [variationList, setVariationList] = useState<VariationItem[]>([]);
  const [variationError, setVariationError] = React.useState<VariationError[]>(
    [],
  );
  const { t } = useTranslation("inventories");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = React.useState({
    quantity: "",
    rate: "",
    costPrice: "",
  });
  const [oldQuantity, setOldQuantity] = useState(0);
  const [oldVariationQuantity, setOldVariationQuantity] = useState([]);

  const fetchInventoryItem = async (id: string) => {
    setIsLoading(true);
    try {
      const res = await getInventory(id);
      setInventories({
        ...inventories,
        name: res.name,
        rate: res.rate,
        costPrice: res.costPrice,
        quantity: "0",
        date: res.createdAt,
      });
      setOldQuantity(res?.quantity);
      setVariationList(
        res.variations.map((item: any) => ({
          ...item,
          quantity: 0,
        })),
      );
      setOldVariationQuantity(
        res.variations.map((variation: VariationItem) => variation?.quantity),
      );
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      showToast("Failed to fetch variations", "error");
    }
  };

  const handleRestock = async () => {
    const newRate = convertToFloat(inventories.rate);
    const newQuantity = convertToFloat(inventories.quantity);
    const newCostPrice = convertToFloat(inventories.costPrice);

    const newVariationList = variationList.map((item: any) => ({
      ...item,
      price: convertToFloat(item.price),
      costPrice: convertToFloat(item.costPrice),
      quantity: convertToFloat(item.quantity),
    }));

    const { hasErrors, errors } = validateRestock({
      rate: newRate,
      quantity: newQuantity,
      costPrice: newCostPrice,
    });
    const variationErrorMsg = ValidateVariation(newVariationList);
    setVariationError([...variationErrorMsg]);

    if (hasErrors && newVariationList.length === 0) {
      setError(errors);
      return;
    }

    if (variationErrorMsg.length === 0) {
      try {
        setIsLoading(true);
        const newInventory = newVariationList.length
          ? {
              variations: newVariationList,
              date: new Date(inventories?.date || "").toISOString(),
            }
          : {
              rate: newRate,
              costPrice: newCostPrice,
              quantity: newQuantity,
              date: new Date(inventories?.date || "").toISOString(),
            };
        if (!restockId) return;
        const response = await restockInventory(restockId, newInventory);
        setIsLoading(false);
        setInventories({
          name: "",
          rate: "",
          quantity: "",
          costPrice: "",
          variations: [],
        });
        setVariationList([]);
        setOpenModal(false);
        fetchDetail?.(restockId);
        if (fetchInventory) fetchInventory();
        showToast(t("inventory.restockSuccessful"), "success");
      } catch (error: any) {
        if (error.message === "Inventory already exists") {
          showToast(error.message, "error");
        } else {
          showToast("Unable to restock inventory", "info");
        }
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (restockId) {
      fetchInventoryItem(restockId);
    }
  }, [restockId]);

  const { fetchVariations } = useInventoryStore((state) => ({
    fetchVariations: state.fetchVariation,
  }));

  const { variations } = useInventoryStore((state) => ({
    variations: state.variations,
  }));

  useEffect(() => {
    fetchVariations();
  }, [fetchVariations]);

  function showCancelConfirmation() {
    setOpenModal(false);
  }

  const cx =
    variationList?.length > 0
      ? ""
      : "fixed bottom-12 left-10 right-10 mb-34 flex gap-4 md:relative md:left-0 md:right-0 md:bottom-0";
  return (
    <div className="bg-white w-[770px] max-h-[600px] m-4 overflow-auto relative p-5 rounded-2xl">
      {isLoading ? (
        <div className="flex items-center my-auto min-h-[420px] justify-center h-full">
          <LoadingSpinner />
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between py-[7px] mb-2 px-4">
            <h1 className="text-[20px] font-semibold">Restock Product</h1>
            <div
              className="hidden md:block cursor-pointer"
              onClick={() => setOpenModal(false)}
            >
              <CloseIcon width="30" height="30" />
            </div>
          </div>

          <hr className="pt-3 border-[#EAECF0]" />

          <div className="px-4 mb-4 space-y-3">
            <p className="text-[20px] font-semibold">{inventories.name}</p>
            {variationList.length > 0 && (
              <div className="flex">
                <div className="bg-[#F2F4F7] p-2 rounded-lg font-medium text-sm flex gap-2">
                  Variation <span>|</span>{" "}
                  <span className="text-[#0469C5]">
                    {variationList[0]?.name}
                  </span>
                </div>
              </div>
            )}
          </div>

          <hr className="pt-3 border-[#EAECF0]" />

          <EditInventoryForm
            inventory={inventories}
            variations={variationList}
            setVariations={setVariationList}
            setInventories={setInventories}
            variationList={variations}
            error={error}
            setError={setError}
            variationError={variationError}
          />

          <div className="hidden md:block mt-[24px] md:mt-[40px] border border-[#EAECF0] px-[10px] mb-10 md:mb-6"></div>

          <div className="md:mt-6 px-4 md:flex md:justify-end">
            <div className=" l mb-34 flex gap-4 md:relative md:left-0 md:right-0 md:bottom-0">
              <ButtonCTA onClick={showCancelConfirmation} variant="outline">
                Cancel
              </ButtonCTA>

              <ButtonCTA onClick={handleRestock}>Restock</ButtonCTA>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
 
export default Restock;
